import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BIBLE_LANGUAGES } from '../../constants';
import { db } from '../../firebase';
import {
  calculateDeckProgress,
  commitMemoryAction,
  countNeverLearnedVerses,
  createMemorizationSessionQueue,
  getNextLearningStage,
  getNextQueueWakeMs,
  getPreviousLearningStage,
  loadActiveCardMemory,
  loadDeckDailyLearningCount,
  loadDeckMemoryStatus,
  MEMORY_LEARNING_STAGES,
  updateQueueAfterMemoryAction,
} from '../../utils/memorization_scheduler';
import { getTodayDateString } from '../../utils/deck_pace_utils';
import {
  acceptInitialsInput,
  formatMemoryCountdown,
  getBlankTokenIds,
  getFetchedVerse,
  getFirstVerseWord,
  getVerseInitials,
} from '../../utils/memorization_practice_utils';
import { fetchLSMVersesWithRetry } from '../../utils/lsm_api';
import { getBibleLanguageFromCookie } from '../../utils/theme_utils';
import { formatVerseHtml, tokenizeVerseText } from '../../utils/verse_format_utils';
import { formatVerseTuple } from '../../utils/verse_reference_parser';
import { Icon } from '../Icon';

const cardStyle = {
  display: 'block',
  width: '100%',
  maxWidth: '600px',
  boxSizing: 'border-box',
  padding: '24px 20px',
};

const MAX_CACHED_VERSES = 50;

function addToBoundedCache(cache, key, value) {
  const updated = new Map(cache);
  updated.delete(key);
  updated.set(key, value);
  while (updated.size > MAX_CACHED_VERSES) {
    updated.delete(updated.keys().next().value);
  }
  return updated;
}

const actionRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '8px',
  marginTop: '20px',
};

function VerseText({ text }) {
  return (
    <div
      style={{
        fontSize: 'clamp(1.15rem, 4.5vw, 1.5rem)',
        lineHeight: 1.65,
        textAlign: 'left',
      }}
      dangerouslySetInnerHTML={{ __html: formatVerseHtml(text) }}
    />
  );
}

function CenteredState({ icon, title, children }) {
  return (
    <div style={{ minHeight: '100%', display: 'grid', placeItems: 'center' }}>
      <md-elevated-card style={{ ...cardStyle, textAlign: 'center' }}>
        <Icon
          name={icon}
          size="hero"
          style={{ color: 'var(--md-sys-color-primary)' }}
        />
        <h2 style={{ margin: '12px 0 8px' }}>{title}</h2>
        {children}
      </md-elevated-card>
    </div>
  );
}

function ProgressSummary({ progress, verseCount }) {
  return (
    <div
      aria-label="Deck memorization progress"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '12px',
        margin: '20px 0',
      }}
    >
      {[
        ['Learned', progress.learned_count],
        ['Retained', progress.retained_count],
      ].map(([label, value]) => (
        <div
          key={label}
          style={{
            padding: '14px 8px',
            borderRadius: '16px',
            backgroundColor: 'var(--md-sys-color-secondary-container)',
            color: 'var(--md-sys-color-on-secondary-container)',
          }}
        >
          <strong style={{ display: 'block', fontSize: '1.35rem' }}>
            {value}/{verseCount}
          </strong>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function BlankPractice({ text, fraction, revealedTokenIds, onReveal }) {
  const tokens = useMemo(() => tokenizeVerseText(text), [text]);
  const hiddenTokenIds = useMemo(
    () => getBlankTokenIds(text, fraction),
    [fraction, text]
  );
  return (
    <div
      aria-label={`${Math.round(fraction * 100)} percent blank verse`}
      style={{
        fontSize: 'clamp(1.1rem, 4.5vw, 1.45rem)',
        lineHeight: 2,
        textAlign: 'left',
      }}
    >
      {tokens.map(token => {
        const hidden = hiddenTokenIds.has(token.id) && !revealedTokenIds.has(token.id);
        const content = token.italic ? <em>{token.text}</em> : token.text;
        return (
          <React.Fragment key={token.id}>
            {hidden ? (
              <button
                type="button"
                aria-label={`Reveal word ${token.id + 1}`}
                onClick={() => onReveal(token.id)}
                style={{
                  minWidth: `${Math.max(3, Array.from(token.text).length)}ch`,
                  border: 0,
                  borderBottom: '2px solid var(--md-sys-color-primary)',
                  background: 'var(--md-sys-color-primary-container)',
                  color: 'var(--md-sys-color-on-primary-container)',
                  font: 'inherit',
                  cursor: 'pointer',
                }}
              >
                •••
              </button>
            ) : content}
            {' '}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function DeckMemorizeTab({
  deck,
  membership,
  user,
  busy = false,
  onSignIn,
  onJoin,
  onProgressChange,
}) {
  const [language] = useState(
    () => getBibleLanguageFromCookie() || BIBLE_LANGUAGES.ENGLISH
  );
  const [loading, setLoading] = useState(Boolean(user && membership));
  const [loadError, setLoadError] = useState('');
  const [memoryStatus, setMemoryStatus] = useState(new Map());
  const [progress, setProgress] = useState({ learned_count: 0, retained_count: 0 });
  const [learnedToday, setLearnedToday] = useState(0);
  const [sessionQueue, setSessionQueue] = useState([]);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionNow, setSessionNow] = useState(() => Date.now());
  const [activeMemory, setActiveMemory] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState('');
  const [cardLoadRevision, setCardLoadRevision] = useState(0);
  const [verseCache, setVerseCache] = useState(new Map());
  const [learningStage, setLearningStage] = useState(MEMORY_LEARNING_STAGES[0]);
  const [revealedTokenIds, setRevealedTokenIds] = useState(new Set());
  const [initialsInput, setInitialsInput] = useState('');
  const [recallRevealed, setRecallRevealed] = useState(false);
  const [firstWordRevealed, setFirstWordRevealed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [writeError, setWriteError] = useState('');
  const [toast, setToast] = useState('');

  const paceTarget = Number.isInteger(deck.pace?.verses_per_day)
    ? deck.pace.verses_per_day
    : 1;
  const activeCard = sessionStarted
    ? sessionQueue.find(card => card.available_at <= sessionNow)
    : null;
  const activeKey = activeCard?.verse_key || null;
  const activeCacheKey = activeKey === null ? null : `${language}:${activeKey}`;
  const activeVerse = activeCacheKey ? verseCache.get(activeCacheKey) : null;
  const nextWakeMs = sessionStarted && !activeCard
    ? getNextQueueWakeMs(sessionQueue, sessionNow)
    : null;

  const showToast = message => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3500);
  };

  const loadSession = useCallback(async () => {
    if (!user || !membership) {
      return;
    }
    setLoading(true);
    setLoadError('');
    setSessionStarted(false);
    try {
      const now = Date.now();
      const today = getTodayDateString(new Date(now));
      const [nextMemoryStatus, nextLearnedToday] = await Promise.all([
        loadDeckMemoryStatus(
          db,
          user.uid,
          deck.verses,
          now
        ),
        loadDeckDailyLearningCount(
          db,
          user.uid,
          deck.id,
          today
        ),
      ]);
      const nextProgress = calculateDeckProgress(deck.verses, nextMemoryStatus, now);
      onProgressChange?.({
        ...nextProgress,
        progress_revision: deck.content_revision,
        progress_updated_at: now,
      });
      setMemoryStatus(nextMemoryStatus);
      setProgress(nextProgress);
      setLearnedToday(nextLearnedToday);
      setSessionQueue(createMemorizationSessionQueue(
        deck.verses,
        nextMemoryStatus,
        {
          paceTarget,
          newVerseLimit: Math.max(0, paceTarget - nextLearnedToday),
        },
        now
      ));
      setSessionNow(now);
    } catch (error) {
      setLoadError(error.message || 'Unable to load memorization progress.');
    } finally {
      setLoading(false);
    }
  }, [
    deck.content_revision,
    deck.id,
    deck.verses,
    membership,
    onProgressChange,
    paceTarget,
    user,
  ]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    if (!sessionStarted || activeCard || nextWakeMs === null || nextWakeMs <= 0) {
      return undefined;
    }
    const timerId = window.setTimeout(
      () => setSessionNow(Date.now()),
      Math.min(nextWakeMs, 1000, 2147483647)
    );
    return () => window.clearTimeout(timerId);
  }, [activeCard, nextWakeMs, sessionStarted]);

  useEffect(() => {
    setLearningStage(MEMORY_LEARNING_STAGES[0]);
    setRevealedTokenIds(new Set());
    setInitialsInput('');
    setRecallRevealed(false);
    setFirstWordRevealed(false);
    setWriteError('');
  }, [activeKey]);

  useEffect(() => {
    if (!sessionStarted || !activeCard || !user) {
      setActiveMemory(null);
      return undefined;
    }
    let active = true;
    setCardLoading(true);
    setCardError('');
    loadActiveCardMemory(
      db,
      user.uid,
      activeCard.tuple,
      memoryStatus,
      Date.now()
    )
      .then(memory => {
        if (active) {
          setActiveMemory(memory);
        }
      })
      .catch(error => {
        if (active) {
          setCardError(error.message || 'Unable to load this memory card.');
        }
      })
      .finally(() => {
        if (active) {
          setCardLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [
    activeCard,
    activeKey,
    cardLoadRevision,
    memoryStatus,
    sessionStarted,
    user,
  ]);

  useEffect(() => {
    if (!sessionStarted || !activeCard || activeVerse) {
      return undefined;
    }
    let active = true;
    const fallbackReference = formatVerseTuple(activeCard.tuple);
    fetchLSMVersesWithRetry(fallbackReference, 'default', language)
      .then(response => {
        const fetchedVerse = getFetchedVerse(response, fallbackReference);
        if (active) {
          setVerseCache(current => addToBoundedCache(
            current,
            activeCacheKey,
            fetchedVerse
          ));
        }
      })
      .catch(error => {
        if (active) {
          setCardError(error.message || 'Unable to load Bible text.');
        }
      });
    return () => {
      active = false;
    };
  }, [
    activeCacheKey,
    activeCard,
    activeKey,
    activeVerse,
    cardLoadRevision,
    language,
    sessionStarted,
  ]);

  const persistAction = async action => {
    if (!activeMemory || saving) {
      return;
    }
    const card = activeCard;
    const actionDate = getTodayDateString(new Date());
    setSaving(true);
    setWriteError('');
    try {
      const { memory, dailyLearningCount, countedAsNewToday } = await commitMemoryAction(
        db,
        user.uid,
        deck.id,
        card.tuple,
        action,
        { dailyLearningDate: actionDate }
      );
      const nextMemoryStatus = new Map(memoryStatus);
      nextMemoryStatus.set(card.verse_key, memory);
      const nextProgress = calculateDeckProgress(
        deck.verses,
        nextMemoryStatus,
        memory.updated_at
      );
      setMemoryStatus(nextMemoryStatus);
      setProgress(nextProgress);
      setSessionQueue(updateQueueAfterMemoryAction(
        sessionQueue,
        card,
        memory,
        action,
        memory.updated_at
      ));
      setSessionNow(Date.now());
      if (countedAsNewToday) {
        setLearnedToday(dailyLearningCount);
      }
      onProgressChange?.({
        ...nextProgress,
        progress_revision: deck.content_revision,
        progress_updated_at: memory.updated_at,
      });
    } catch (error) {
      const message = error.message || 'Unable to save memorization progress.';
      setWriteError(message);
      showToast(message);
    } finally {
      setSaving(false);
    }
  };

  const startExtraSession = () => {
    const now = Date.now();
    setSessionQueue(createMemorizationSessionQueue(
      deck.verses,
      memoryStatus,
      {
        paceTarget,
        newVerseLimit: paceTarget,
        fillWithFutureReviews: false,
      },
      now
    ));
    setSessionNow(now);
    setSessionStarted(true);
  };

  if (!user) {
    return (
      <div style={{ height: '100%', overflow: 'auto', padding: '16px', boxSizing: 'border-box' }}>
        <CenteredState icon="signIn" title="Sign in to memorize">
          <p style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
            Sign in to save your memory schedule and deck progress.
          </p>
          <md-filled-button onClick={onSignIn}>Sign In</md-filled-button>
        </CenteredState>
      </div>
    );
  }

  if (!membership) {
    return (
      <div style={{ height: '100%', overflow: 'auto', padding: '16px', boxSizing: 'border-box' }}>
        <CenteredState icon="joinDeck" title="Join this deck">
          <p style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
            Join to begin memorizing and save your progress.
          </p>
          <md-filled-button {...(busy ? { disabled: true } : {})} onClick={onJoin}>
            <Icon name="joinDeck" slot="icon" />
            {busy ? 'Joining…' : 'Join Deck'}
          </md-filled-button>
        </CenteredState>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ height: '100%', display: 'grid', placeItems: 'center' }}>
        <div style={{ textAlign: 'center' }} role="status">
          <md-circular-progress indeterminate></md-circular-progress>
          <p>Loading memorization progress…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ height: '100%', overflow: 'auto', padding: '16px', boxSizing: 'border-box' }}>
        <CenteredState icon="error" title="Progress unavailable">
          <p role="alert">{loadError}</p>
          <md-filled-button onClick={loadSession}>Retry</md-filled-button>
        </CenteredState>
      </div>
    );
  }

  const dailyRemaining = Math.max(0, paceTarget - learnedToday);
  const neverLearnedCount = countNeverLearnedVerses(deck.verses, memoryStatus);

  if (sessionQueue.length === 0) {
    return (
      <div style={{ height: '100%', overflow: 'auto', padding: '16px', boxSizing: 'border-box' }}>
        <CenteredState icon="check" title="Today’s session complete">
          <p>You completed today’s new verses and scheduled review work.</p>
          <ProgressSummary progress={progress} verseCount={deck.verses.length} />
          {neverLearnedCount > 0 && (
            <md-outlined-button onClick={startExtraSession}>
              Start Extra Session
            </md-outlined-button>
          )}
        </CenteredState>
      </div>
    );
  }

  if (!sessionStarted) {
    const overdueCount = sessionQueue.filter(card => (
      card.mode === 'recall'
      && card.scheduled_for !== null
      && card.scheduled_for <= sessionNow
    )).length;
    const relearningCount = sessionQueue.filter(card => card.is_relearning).length;
    const newCount = sessionQueue.filter(card => (
      card.mode === 'learn' && !card.is_relearning
    )).length;
    return (
      <div style={{ height: '100%', overflow: 'auto', padding: '16px', boxSizing: 'border-box' }}>
        <md-elevated-card style={{ ...cardStyle, margin: '0 auto', textAlign: 'center' }}>
          <Icon
            name="memorizeTab"
            size="hero"
            style={{ color: 'var(--md-sys-color-primary)' }}
          />
          <h2 style={{ margin: '10px 0 4px' }}>Today’s session</h2>
          <p style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
            {overdueCount} overdue
            {relearningCount > 0 ? ` · ${relearningCount} relearning` : ''}
            {` · ${newCount} new · ${sessionQueue.length} total`}
          </p>
          <p style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
            {learnedToday} learned today · {dailyRemaining} daily new remaining
          </p>
          <ProgressSummary progress={progress} verseCount={deck.verses.length} />
          {sessionQueue.length > 0 ? (
            <md-filled-button
              onClick={() => {
                setSessionNow(Date.now());
                setSessionStarted(true);
              }}
            >
              Start Session
            </md-filled-button>
          ) : (
            <p>There are no cards scheduled for this session.</p>
          )}
        </md-elevated-card>
      </div>
    );
  }

  if (!activeCard) {
    return (
      <div style={{ height: '100%', overflow: 'auto', padding: '16px', boxSizing: 'border-box' }}>
        <CenteredState icon="calendar" title="Next recall is coming up">
          <p>Keep this session open. Your next card will appear in:</p>
          <strong
            aria-live="polite"
            style={{ display: 'block', fontSize: '2rem', margin: '16px' }}
          >
            {formatMemoryCountdown(nextWakeMs)}
          </strong>
        </CenteredState>
      </div>
    );
  }

  if (cardLoading || !activeMemory || !activeVerse) {
    return (
      <div style={{ height: '100%', display: 'grid', placeItems: 'center', padding: '16px' }}>
        {cardError ? (
          <div style={{ textAlign: 'center' }}>
            <Icon name="error" size="hero" style={{ color: 'var(--md-sys-color-error)' }} />
            <p role="alert">{cardError}</p>
            <md-filled-button
              onClick={() => {
                setCardError('');
                setCardLoadRevision(value => value + 1);
              }}
            >
              Retry
            </md-filled-button>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }} role="status">
            <md-circular-progress indeterminate></md-circular-progress>
            <p>Loading memory card…</p>
          </div>
        )}
      </div>
    );
  }

  const moveLearningStage = direction => {
    setLearningStage(current => (
      direction === 'next'
        ? getNextLearningStage(current)
        : getPreviousLearningStage(current)
    ));
    setRevealedTokenIds(new Set());
    setInitialsInput('');
  };
  const reference = activeVerse.reference || formatVerseTuple(activeCard.tuple);
  const firstWord = getFirstVerseWord(activeVerse.text);
  const correctInitials = getVerseInitials(activeVerse.text);
  const blankFractions = {
    blank_25: 0.25,
    blank_50: 0.5,
    blank_75: 0.75,
  };

  return (
    <div style={{
      height: '100%',
      overflow: 'auto',
      padding: '16px',
      boxSizing: 'border-box',
    }}>
      <md-elevated-card style={{ ...cardStyle, margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '12px',
          color: 'var(--md-sys-color-on-surface-variant)',
        }}>
          <span>{reference}</span>
          <span>{sessionQueue.length} remaining</span>
        </div>

        {activeCard.mode === 'recall' ? (
          <div style={{ textAlign: 'center', marginTop: '28px' }}>
            {!recallRevealed ? (
              <>
                <h2>Recall this verse: {reference}</h2>
                <p>Recite the verse from memory before revealing the text.</p>
                {firstWordRevealed && (
                  <p role="status">
                    First word: <strong>{firstWord}</strong>
                  </p>
                )}
                <div style={actionRowStyle}>
                  <md-outlined-button
                    {...((firstWordRevealed || !firstWord) ? { disabled: true } : {})}
                    onClick={() => setFirstWordRevealed(true)}
                  >
                    Show first word
                  </md-outlined-button>
                  <md-filled-button onClick={() => setRecallRevealed(true)}>
                    Show Verse
                  </md-filled-button>
                </div>
              </>
            ) : (
              <>
                <VerseText text={activeVerse.text} />
                <p>How accurately did you recall it?</p>
                <div style={actionRowStyle} aria-label="Recall rating">
                  <md-outlined-button
                    {...(saving ? { disabled: true } : {})}
                    onClick={() => persistAction('hard')}
                  >
                    Hard
                  </md-outlined-button>
                  <md-filled-tonal-button
                    {...(saving ? { disabled: true } : {})}
                    onClick={() => persistAction('okay')}
                  >
                    Okay
                  </md-filled-tonal-button>
                  <md-filled-button
                    {...(saving ? { disabled: true } : {})}
                    onClick={() => persistAction('easy')}
                  >
                    Easy
                  </md-filled-button>
                  <md-outlined-button
                    {...(saving ? { disabled: true } : {})}
                    onClick={() => persistAction('forgot')}
                  >
                    Forgot
                  </md-outlined-button>
                  <md-text-button
                    {...(saving ? { disabled: true } : {})}
                    onClick={() => persistAction('forgot_learn_again')}
                  >
                    Forgot — learn again
                  </md-text-button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div style={{ marginTop: '24px' }}>
            {learningStage === 'pray' && (
              <>
                <h2>Pray-read and explain</h2>
                <p>Pray with the verse, then explain its meaning in your own words.</p>
                <VerseText text={activeVerse.text} />
              </>
            )}
            {Object.prototype.hasOwnProperty.call(blankFractions, learningStage) && (
              <>
                <h2>{Math.round(blankFractions[learningStage] * 100)}% hidden</h2>
                <p>Recall each blank. Select a blank if you need to reveal it.</p>
                <BlankPractice
                  text={activeVerse.text}
                  fraction={blankFractions[learningStage]}
                  revealedTokenIds={revealedTokenIds}
                  onReveal={tokenId => setRevealedTokenIds(current => (
                    new Set([...current, tokenId])
                  ))}
                />
              </>
            )}
            {learningStage === 'initials' && (
              <>
                <h2>Type the initials</h2>
                <p>Enter the first letter or visible character of every word.</p>
                <label htmlFor="verse-initials" style={{ display: 'block', marginBottom: '8px' }}>
                  Verse initials
                </label>
                <input
                  id="verse-initials"
                  value={initialsInput}
                  onChange={event => {
                    const accepted = acceptInitialsInput(event.target.value, correctInitials);
                    if (accepted !== null) {
                      setInitialsInput(accepted);
                    }
                  }}
                  autoComplete="off"
                  autoCapitalize="off"
                  aria-describedby="initials-progress"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '14px',
                    border: '1px solid var(--md-sys-color-outline)',
                    borderRadius: '12px',
                    backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
                    color: 'var(--md-sys-color-on-surface)',
                    font: 'inherit',
                    letterSpacing: '0.12em',
                  }}
                />
                <p id="initials-progress" aria-live="polite">
                  {initialsInput.length} of {correctInitials.length} correct
                </p>
              </>
            )}
            {learningStage === 'confirm' && (
              <>
                <h2>Confirm from memory</h2>
                <p>Recite the complete verse once more before confirming it learned.</p>
                <VerseText text={activeVerse.text} />
              </>
            )}
            <div style={actionRowStyle}>
              <md-text-button
                {...(saving ? { disabled: true } : {})}
                onClick={() => {
                  if (learningStage === MEMORY_LEARNING_STAGES[0]) {
                    setSessionStarted(false);
                  } else {
                    moveLearningStage('back');
                  }
                }}
              >
                Back
              </md-text-button>
              {learningStage === 'confirm' ? (
                <md-filled-button
                  {...(saving ? { disabled: true } : {})}
                  onClick={() => persistAction('confirm')}
                >
                  Confirm Learned
                </md-filled-button>
              ) : (
                <md-filled-button
                  {...((
                    saving
                    || (learningStage === 'initials'
                      && initialsInput.toLocaleLowerCase()
                        !== correctInitials.toLocaleLowerCase())
                  ) ? { disabled: true } : {})}
                  onClick={() => moveLearningStage('next')}
                >
                  Continue
                </md-filled-button>
              )}
            </div>
          </div>
        )}

        {saving && <p role="status">Saving progress…</p>}
        {writeError && (
          <p role="alert" style={{ color: 'var(--md-sys-color-error)' }}>
            {writeError}
          </p>
        )}
      </md-elevated-card>
      {toast && (
        <div
          role="status"
          aria-live="assertive"
          style={{
            position: 'fixed',
            left: '50%',
            bottom: '82px',
            transform: 'translateX(-50%)',
            maxWidth: 'calc(100vw - 32px)',
            padding: '12px 18px',
            borderRadius: '24px',
            backgroundColor: 'var(--md-sys-color-inverse-surface)',
            color: 'var(--md-sys-color-inverse-on-surface)',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
